import { Badge, Button, ButtonProps, createPolymorphicComponent, Group, Text, useMantineTheme } from '@mantine/core';
import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import {
  faFilter as faFilterSolid,
  faArrowDownLeftAndArrowUpRightToCenter,
  faArrowUpRightAndArrowDownLeftFromCenter,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classes from './FilterAddButton.module.css';
import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

type FilterAddButtonProps = ButtonProps &
  Partial<{
    count: number;
    onToggleExpand: () => void;
    expanded: boolean;
  }>;

const _FilterAddButton = React.forwardRef<HTMLButtonElement, FilterAddButtonProps>(
  ({ count, className, expanded, onToggleExpand, ...props }: FilterAddButtonProps, ref) => {
    const { t } = useTranslation('data-grid');
    const theme = useMantineTheme();

    const handleToggleExpand = (e: React.MouseEvent<SVGSVGElement>) => {
      e.stopPropagation();
      onToggleExpand?.();
    };

    const rightSection = (
      <Group gap={10}>
        {count > 0 ? (
          <Badge radius="sm" variant="filled" color="light.3" p={0} fz={10} c="gray.5" fw={400} px={4} h={14}>
            {count}
          </Badge>
        ) : null}
        {count > 3 && onToggleExpand ? (
          <FontAwesomeIcon
            onClick={(e) => handleToggleExpand(e)}
            size="sm"
            icon={expanded ? faArrowDownLeftAndArrowUpRightToCenter : faArrowUpRightAndArrowDownLeftFromCenter}
            color={theme.colors.blue[5]}
          />
        ) : null}
      </Group>
    );

    return (
      <Button
        className={clsx(classes.filterAddButton, className)}
        ref={ref}
        leftSection={
          <FontAwesomeIcon
            icon={count > 0 ? faFilterSolid : faFilter}
            color={count > 0 ? theme.colors.blue[5] : theme.colors.gray[5]}
          />
        }
        rightSection={rightSection}
        color="light.1"
        size="xs"
        {...props}
      >
        <Text className={classes.text} c="gray" component="span" lh="unset" fw={500} fz={12}>
          {t('filters')}
        </Text>
      </Button>
    );
  },
);

export const FilterAddButton = createPolymorphicComponent<'button', FilterAddButtonProps>(_FilterAddButton);
